<template>
  <figure :id="`video-player-${uuid}`">
    <video :key="videoId"
      controls
      preload="auto"
      class="video-js"
      ref="player"
      @loadeddata="markVideoReady"
      controlsList="nodownload"
      playsinline
      :aria-label="ariaLabel"
    >
      <track v-if="transcriptSource && hasTranscript"
        kind="captions"
        :src="transcriptSource"
        srclang="en"
        label="English"
        default
      />
      <p class="vjs-no-js">
        To view this video please enable JavaScript, and consider upgrading to a
        web browser that
        <a href="https://videojs.com/html5-video-support/" target="_blank" rel="noopener noreferrer">supports HTML5 video</a>
      </p>
    </video>
  </figure>
</template>

<style>
.video-js .vjs-menu-item-text {
  color: var(--kate-type-white);
}

.vjs-menu li.vjs-selected {
  background: #2b333f !important;
}

.vjs-text-track-settings fieldset span > select,
.vjs-track-setting > select {
  color: var(--kate-type-dark) !important;
}
</style>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import videojs from 'video.js';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'video.js/dist/video-js.css';

export default {
  props: {
    videoSrc: {
      type: String,
      required: true,
    },
    videoId: {
      type: Number,
      required: true,
    },
    ariaLabel: {
      type: String,
      default: 'Video player',
    },
    transcriptSource: {
      type: String,
    },
    hasTranscript: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    videoSrc: {
      handler(newUrl) {
        if (!newUrl) return;
        this.$nextTick(() => {
          this.initVideoJs();
          this.setPlayerSource();
        });
      },
      immediate: true,
    },
    transcriptSource: {
      handler(newUrl) {
        if (!newUrl) return;
        this.$nextTick(() => {
          this.initVideoJs();
          this.addTranscriptTrack(newUrl);
        });
      },
      immediate: true,
    },
  },

  data() {
    return {
      player: null,
      uuid: null,
    };
  },

  beforeMount() {
    this.uuid = this.$.uid;
  },

  mounted() {
    this.initVideoJs();
  },

  beforeUnmount() {
    if (this.player) {
      this.player.dispose();
      this.player = null;
    }
  },

  methods: {
    markVideoReady() {
      this.$emit('ready');
    },

    initVideoJs() {
      if (!this.$refs.player) return;
      if (this.player) return;
      const SKIP_SECONDS = 10;
      this.player = videojs(this.$refs.player, {
        controls: true,
        preload: 'auto',
        fluid: true,
        playbackRates: [0.5, 1, 1.5, 2, 2.5, 3],
        controlBar: {
          skipButtons: {
            forward: SKIP_SECONDS,
            backward: SKIP_SECONDS,
          },
          progressControl: {
            seekBar: {
              stepSeconds: SKIP_SECONDS,
            },
          },
        },
        userActions: {
          hotkeys(event) {
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              event.stopPropagation();
              this.currentTime(Math.max(0, this.currentTime() - SKIP_SECONDS));
              return;
            }
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              event.stopPropagation();
              const duration = this.duration();
              if (Number.isNaN(duration)) return;
              this.currentTime(Math.min(duration, this.currentTime() + SKIP_SECONDS));
              return;
            }
            this.handleHotkeys(event);
          },
        },
      });
      this.setPlayerSource();
      if (this.transcriptSource) {
        this.addTranscriptTrack(this.transcriptSource);
      }
    },

    setPlayerSource() {
      if (!this.player || !this.videoSrc) return;
      this.player.src(this.getPlayerSource(this.videoSrc));
      this.player.load();
    },

    getPlayerSource(src) {
      const normalizedSrc = (src || '').split('?')[0].split('#')[0].toLowerCase();
      if (normalizedSrc.endsWith('.m3u8')) {
        return { src, type: 'application/x-mpegURL' };
      }
      if (normalizedSrc.endsWith('.mpd')) {
        return { src, type: 'application/dash+xml' };
      }
      if (normalizedSrc.endsWith('.webm')) {
        return { src, type: 'video/webm' };
      }
      return { src, type: 'video/mp4' };
    },

    addTranscriptTrack(url) {
      if (!this.player) {
        return;
      }

      const existingTracks = this.player.remoteTextTracks();
      for (let i = existingTracks.length - 1; i >= 0; i--) {
        this.player.removeRemoteTextTrack(existingTracks[i]);
      }

      this.player.addRemoteTextTrack({
        kind: 'captions',
        src: url,
        srclang: 'en',
        label: 'English',
        default: true,
      }, false);
    },
  },
};
</script>
